import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { featuredCampaigns, campaignCategories } from '@/mocks/homeData';

type SortOption = 'newest' | 'urgent' | 'most-raised' | 'most-donors' | 'goal-low' | 'goal-high';

const sortOptions: { value: SortOption; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'urgent', label: 'Most Urgent' },
  { value: 'most-raised', label: 'Most Raised' },
  { value: 'most-donors', label: 'Most Donors' },
  { value: 'goal-low', label: 'Goal: Low to High' },
  { value: 'goal-high', label: 'Goal: High to Low' },
];

const urgencyConfig: Record<string, { label: string; classes: string }> = {
  critical: { label: 'Critical', classes: 'bg-red-100 text-red-700' },
  high: { label: 'Urgent', classes: 'bg-orange-100 text-orange-700' },
  medium: { label: 'Active', classes: 'bg-blue-100 text-blue-700' },
  low: { label: 'Ongoing', classes: 'bg-green-100 text-green-700' },
};

export default function CampaignsPage() {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sort, setSort] = useState<SortOption>('newest');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const filteredCampaigns = useMemo(() => {
    let result = [...featuredCampaigns];

    if (selectedCategory !== 'All') {
      result = result.filter((c) => c.category === selectedCategory);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.category.toLowerCase().includes(q) ||
          c.story.toLowerCase().includes(q)
      );
    }

    switch (sort) {
      case 'urgent':
        result.sort((a, b) => {
          const order: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
          return order[a.urgency] - order[b.urgency];
        });
        break;
      case 'most-raised':
        result.sort((a, b) => b.raised - a.raised);
        break;
      case 'most-donors':
        result.sort((a, b) => b.donors - a.donors);
        break;
      case 'goal-low':
        result.sort((a, b) => a.goal - b.goal);
        break;
      case 'goal-high':
        result.sort((a, b) => b.goal - a.goal);
        break;
      default:
        break;
    }

    return result;
  }, [search, selectedCategory, sort]);

  const allCategories = ['All', ...campaignCategories.map((c) => c.name)];

  const totalRaised = featuredCampaigns.reduce((sum, c) => sum + c.raised, 0);
  const totalDonors = featuredCampaigns.reduce((sum, c) => sum + c.donors, 0);

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-20 pb-14 md:pt-28 md:pb-20 bg-gradient-to-br from-primary-500 via-primary-400 to-accent-400 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4 blur-3xl"></div>
          <div className="relative max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 text-center">
            <span className="inline-block px-4 py-1.5 bg-white/20 backdrop-blur-sm text-white/90 text-xs font-medium rounded-full mb-4">
              <i className="ri-flag-line mr-1.5"></i>Active Campaigns
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">Find a Cause You Care About</h1>
            <p className="text-white/80 text-base md:text-lg max-w-2xl mx-auto mb-8">
              Browse our verified campaigns and support the cause that speaks to your heart. Every donation creates real, measurable impact.
            </p>
            <div className="flex items-center justify-center gap-6 flex-wrap">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">{featuredCampaigns.length}</div>
                <div className="text-white/70 text-xs">Active Campaigns</div>
              </div>
              <div className="w-px h-10 bg-white/20"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">₹{(totalRaised / 100000).toFixed(1)}L+</div>
                <div className="text-white/70 text-xs">Total Raised</div>
              </div>
              <div className="w-px h-10 bg-white/20"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">{totalDonors.toLocaleString()}+</div>
                <div className="text-white/70 text-xs">Donors</div>
              </div>
            </div>
          </div>
        </section>

        {/* Search & Filter Bar */}
        <section className="sticky top-16 md:top-20 z-30 bg-white/90 backdrop-blur-xl border-b border-background-200">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-3">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="Search campaigns..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-900 placeholder:text-foreground-400"
                />
                {search && (
                  <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-foreground-300 flex items-center justify-center">
                    <i className="ri-close-line text-white text-xs"></i>
                  </button>
                )}
              </div>

              {/* Category filter chips - scrollable */}
              <div className="flex items-center gap-1.5 overflow-x-auto flex-nowrap pb-1 sm:pb-0 scrollbar-hide">
                {allCategories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3.5 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-all duration-200 ${
                      selectedCategory === cat
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* Sort + View toggle */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="relative">
                  <button
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-xl hover:bg-background-200 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-arrow-up-down-line text-sm"></i>
                    {sortOptions.find((o) => o.value === sort)?.label}
                    <i className={`ri-arrow-down-s-line text-xs transition-transform ${showSortDropdown ? 'rotate-180' : ''}`}></i>
                  </button>
                  {showSortDropdown && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowSortDropdown(false)}></div>
                      <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl shadow-lg border border-background-200 py-1 z-50">
                        {sortOptions.map((opt) => (
                          <button
                            key={opt.value}
                            onClick={() => { setSort(opt.value); setShowSortDropdown(false); }}
                            className={`w-full text-left px-4 py-2 text-xs transition-colors ${
                              sort === opt.value
                                ? 'text-primary-600 bg-primary-50 font-medium'
                                : 'text-foreground-600 hover:bg-background-50'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <div className="flex bg-background-100 rounded-xl p-0.5">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-white text-primary-600 shadow-sm' : 'text-foreground-400'}`}
                  >
                    <i className="ri-layout-grid-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${viewMode === 'list' ? 'bg-white text-primary-600 shadow-sm' : 'text-foreground-400'}`}
                  >
                    <i className="ri-list-check text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Campaigns */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            {filteredCampaigns.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-search-line text-foreground-300 text-2xl"></i>
                </div>
                <h3 className="font-heading text-xl font-bold text-foreground-800 mb-2">No campaigns found</h3>
                <p className="text-foreground-500 text-sm mb-4">Try adjusting your search or filters</p>
                <button
                  onClick={() => { setSearch(''); setSelectedCategory('All'); }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-refresh-line"></i>Clear Filters
                </button>
              </div>
            ) : (
              <>
                <p className="text-foreground-500 text-sm mb-6">
                  Showing <strong className="text-foreground-800">{filteredCampaigns.length}</strong> campaign{filteredCampaigns.length !== 1 ? 's' : ''}
                  {selectedCategory !== 'All' && <> in <strong className="text-foreground-800">{selectedCategory}</strong></>}
                </p>

                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {filteredCampaigns.map((campaign) => {
                      const progress = Math.min((campaign.raised / campaign.goal) * 100, 100);
                      const u = urgencyConfig[campaign.urgency] || urgencyConfig.medium;
                      return (
                        <Link
                          key={campaign.id}
                          to={`/campaigns/${campaign.id}`}
                          className="bg-white rounded-2xl overflow-hidden border border-background-200 hover:border-primary-200 hover:shadow-sm transition-all duration-300 group flex flex-col"
                        >
                          <div className="relative aspect-[4/3] overflow-hidden">
                            <img src={campaign.imageUrl} alt={campaign.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                            <span className={`absolute top-3 left-3 px-2.5 py-1 text-[10px] font-bold rounded-full ${u.classes}`}>{u.label}</span>
                            <span className="absolute top-3 right-3 px-2.5 py-1 bg-white/85 backdrop-blur-sm text-foreground-700 text-[10px] font-semibold rounded-full">{campaign.category}</span>
                          </div>
                          <div className="p-5 flex flex-col flex-1">
                            <h3 className="font-heading text-base font-bold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">{campaign.title}</h3>
                            <p className="text-foreground-500 text-xs leading-relaxed mb-4 line-clamp-2">{campaign.story}</p>
                            <div className="mt-auto">
                              <div className="flex items-center justify-between text-xs mb-2">
                                <span className="font-bold text-foreground-950">₹{campaign.raised.toLocaleString()}</span>
                                <span className="text-foreground-400">₹{campaign.goal.toLocaleString()}</span>
                              </div>
                              <div className="w-full h-2 bg-background-200 rounded-full mb-3 overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-primary-500 to-accent-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }}></div>
                              </div>
                              <div className="flex items-center justify-between text-[11px] text-foreground-400">
                                <span>{Math.round(progress)}% raised</span>
                                <span>{campaign.daysLeft} days left</span>
                                <span><i className="ri-user-line mr-1"></i>{campaign.donors.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredCampaigns.map((campaign) => {
                      const progress = Math.min((campaign.raised / campaign.goal) * 100, 100);
                      const u = urgencyConfig[campaign.urgency] || urgencyConfig.medium;
                      return (
                        <Link
                          key={campaign.id}
                          to={`/campaigns/${campaign.id}`}
                          className="flex flex-col sm:flex-row bg-white rounded-2xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300 group"
                        >
                          <div className="relative w-full sm:w-64 flex-shrink-0 aspect-[4/3] sm:aspect-auto overflow-hidden">
                            <img src={campaign.imageUrl} alt={campaign.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                            <span className={`absolute top-3 left-3 px-2.5 py-1 text-[10px] font-bold rounded-full ${u.classes}`}>{u.label}</span>
                          </div>
                          <div className="p-5 flex flex-col flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className="px-2.5 py-0.5 bg-background-100 text-foreground-500 text-[10px] font-medium rounded-full">{campaign.category}</span>
                            </div>
                            <h3 className="font-heading text-base font-bold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors">{campaign.title}</h3>
                            <p className="text-foreground-500 text-xs leading-relaxed mb-4 line-clamp-2">{campaign.story}</p>
                            <div className="flex items-center gap-4 flex-wrap mt-auto">
                              <div className="flex-1 min-w-[180px]">
                                <div className="flex items-center justify-between text-xs mb-1.5">
                                  <span className="font-bold text-foreground-950">₹{campaign.raised.toLocaleString()}</span>
                                  <span className="text-foreground-400">of ₹{campaign.goal.toLocaleString()}</span>
                                </div>
                                <div className="w-full h-2 bg-background-200 rounded-full overflow-hidden">
                                  <div className="h-full bg-gradient-to-r from-primary-500 to-accent-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }}></div>
                                </div>
                              </div>
                              <div className="flex items-center gap-4 text-[11px] text-foreground-400">
                                <span>{Math.round(progress)}%</span>
                                <span>{campaign.daysLeft}d left</span>
                                <span><i className="ri-user-line mr-1"></i>{campaign.donors.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}